import { useLayoutEffect } from "preact/hooks";
import { useXRErrorGuidance } from "@core/hooks/useXRErrorGuidance";
import { Spinner } from "@core/components/Elements";
import Button from "@core/components/Elements/Button/Button";
import ScrollFadeWrapper from "@core/components/Elements/ScrollFadeWrapper/ScrollFadeWrapper";
import MarkdownText from "@core/components/Common/MarkdownText";
import { EjcLaunchButton } from "@core/components/Generics/EjcLaunchButton";
import { CopyToClipboardTrigger } from "@core/components/Generics/CopyToClipboardTrigger";
import ejLogo from "@assets-shared/ej-logo.svg";

/** AR runtime backend. 8thwall renders behind the splash and needs the
 *  whole-screen "Loading.." overlay during asset prep; WebXR just swaps the
 *  launch button for a spinner and keeps the splash content visible. */
export type EjcSplashArBackend = "8thwall" | "webxr";

/** Extra DOM height added below 100dvh so the 8thwall canvas (which renders
 *  behind the splash) doesn't reveal white space when the user drags / scrolls
 *  the bottom edge. Both boilerplates use the same value for visual parity. */
export const EJC_SPLASH_LAYOUT_OFFSET_PX = 200;

interface EjcSplashProps {
  /** Heading text. */
  name: string;
  /** Description / body text. Markdown is supported. */
  description?: string;
  /** Background cover image URL. */
  coverImage?: string;
  /** When true, swap the splash for the loading state (depends on `arBackend`). */
  isLoading?: boolean;
  /** Mobile launch handler. Desktop shows a QR popup regardless. */
  onLaunch: () => void;
  /** Launch button label (default "View in AR"). */
  launchLabel?: string;
  /** When true, the launch button click is suppressed on mobile. */
  launchDisabled?: boolean;
  /** Hide the launch button entirely. Used by bp1 when this same component is
   *  rendered as an in-AR microsite info modal. */
  showLaunchButton?: boolean;
  /** Selects the loading-state UX. See {@link EjcSplashArBackend}. */
  arBackend?: EjcSplashArBackend;
  /** Loading-overlay primary label (8thwall variant only). */
  loadingMessage?: string;
  /** Loading-overlay secondary label (8thwall variant only). May contain HTML. */
  loadingSafetyMessage?: string;
}

const GRAYSCALE_TRANSITION_MS = 600;
const CONTENT_FADE_MS = 300;

export function EjcSplash({
  name,
  description,
  coverImage,
  isLoading = false,
  onLaunch,
  launchLabel,
  launchDisabled = false,
  showLaunchButton = true,
  arBackend = "8thwall",
  loadingMessage = "Loading..",
  loadingSafetyMessage = "Please be aware of your <br /> surrounding when using AR",
}: EjcSplashProps) {
  // The splash is intentionally taller than the viewport (see
  // EJC_SPLASH_LAYOUT_OFFSET_PX) so the 8thwall canvas behind it never reveals
  // white space at the bottom edge. Lock body scrolling while mounted so that
  // extra height doesn't surface a browser scrollbar.
  useLayoutEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // showCopyLinkGuidance: the hook decides whether "open in Chrome" is useful
  // advice (incompatible Android browser) vs. the generic "not supported on this
  // device" message (incapable device, or iOS which hands off to the App Clip).
  const { errorMessage, showCopyLinkGuidance } = useXRErrorGuidance();
  const hasPermissionError = Boolean(errorMessage);
  const normalizedErrorMessage = errorMessage ?? "";
  const [errorPrimary, errorSecondary] = normalizedErrorMessage.split(/<br\s*\/?>\s*<br\s*\/?>/i);
  const hasErrorSecondary = Boolean(errorSecondary && errorSecondary.trim().length > 0);

  // 8thwall: full-content fade-out + overlay. webxr: keep splash visible, only
  // the launch-button slot shows a spinner (handled in the button region below).
  const showLoadingOverlay = isLoading && arBackend === "8thwall";
  const showButtonSpinner = isLoading && arBackend === "webxr" && showLaunchButton;

  const handlePermissionErrorConfirm = () => {
    window.location.reload();
  };

  return (
    <div
      className={`ejc-splash relative w-full overflow-hidden text-white ${hasPermissionError ? "bg-black" : "bg-transparent"}`}
      style={{
        height: `calc(100dvh + ${EJC_SPLASH_LAYOUT_OFFSET_PX}px)`,
        maxHeight: `calc(100dvh + ${EJC_SPLASH_LAYOUT_OFFSET_PX}px)`,
        overscrollBehavior: "none",
      }}
    >
      {!hasPermissionError && (
        <>
          <div
            className="absolute top-0 left-0 right-0 z-0 bg-transparent"
            style={
              coverImage
                ? {
                    height: "100dvh",
                    backgroundImage: `url(${coverImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    filter: showLoadingOverlay ? "grayscale(1)" : "grayscale(0)",
                    transition: `filter ${GRAYSCALE_TRANSITION_MS}ms ease-in-out`,
                  }
                : { height: "100dvh" }
            }
          />
          <div className="absolute inset-0 z-0 bg-black/70" />
        </>
      )}

      {/* EyeJack logo — top-left overlay, sits above the cover/dark layers. */}
      <div className="absolute md:top-10 md:left-10 top-5 left-5 max-w-[147px] md:max-w-[190px] w-full z-20 pointer-events-none">
        <img
          src={ejLogo}
          alt="EyeJack logo"
          className="w-full h-auto"
          style={{ filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.35))" }}
        />
      </div>

      {hasPermissionError ? (
        <div
          className="relative h-full w-full flex flex-col justify-end px-5"
          style={{ paddingBottom: `${EJC_SPLASH_LAYOUT_OFFSET_PX + 38}px` }}
        >
          {showCopyLinkGuidance ? (
            <>
              {/* Same two-line + divider layout as the error screen below
                  (bold heading, hairline divider, lighter subtext) for parity. */}
              <div className="text-[20px] text-center font-light w-full mx-auto animate-fade-in flex flex-col items-center">
                <div className="font-medium">This browser does not support WebXR</div>
                <div className="h-px w-[40px] bg-white/40 my-8" />
                <div className="font-light text-[18px]">
                  Copy the link below and paste into a Chrome browser.
                </div>
              </div>
              <div className="w-full mx-auto mt-[78px] flex justify-center">
                <CopyToClipboardTrigger text={window.location.href} color="white">
                  <div className="border-2 min-w-[250px] border-white border-opacity-30 py-3 px-4 max-w-xs flex gap-x-2 relative cursor-pointer transition-opacity duration-200 hover:opacity-50 hover:border-opacity-50">
                    <span className="link flex-grow text-center max-w-[310px] whitespace-nowrap overflow-hidden text-ellipsis w-full tracking-[1px]">
                      COPY LINK
                    </span>
                  </div>
                </CopyToClipboardTrigger>
              </div>
            </>
          ) : (
            <>
              <div className="text-[20px] text-center font-light w-full mx-auto animate-fade-in flex flex-col items-center">
                <div
                  className="font-medium"
                  dangerouslySetInnerHTML={{ __html: errorPrimary || normalizedErrorMessage }}
                />
                {hasErrorSecondary && <div className="h-px w-[40px] bg-white/40 my-8" />}
                {hasErrorSecondary && (
                  <div
                    className="font-light text-[18px]"
                    dangerouslySetInnerHTML={{ __html: errorSecondary }}
                  />
                )}
              </div>
              <div className="w-full max-w-[260px] mx-auto mt-[78px]">
                <Button
                  onClick={handlePermissionErrorConfirm}
                  className="!rounded-full !bg-transparent !border-2 !border-white !text-white active:!bg-white active:!text-black focus:!bg-white focus:!text-black md:hover:!bg-white md:hover:!text-black"
                >
                  OK
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="relative h-full max-w-[900px] w-full mx-auto">
          {/* Splash content — fades out when the 8thwall loading overlay takes over. */}
          <div
            style={{
              opacity: showLoadingOverlay ? 0 : 1,
              transition: `opacity ${CONTENT_FADE_MS}ms ease-in-out`,
              pointerEvents: showLoadingOverlay ? "none" : "auto",
            }}
            className="absolute inset-0"
          >
            <ScrollFadeWrapper
              className={`absolute z-10 left-5 right-5 md:left-12 md:right-12 overflow-y-auto top-[80px] md:top-[120px]`}
              style={{
                bottom: `${showLaunchButton ? 130 + EJC_SPLASH_LAYOUT_OFFSET_PX : 32 + EJC_SPLASH_LAYOUT_OFFSET_PX}px`,
                overscrollBehaviorY: "none",
              }}
              topFadeHeight="42px"
              bottomFadeHeight={showLaunchButton ? "72px" : "64px"}
            >
              <div className="flex flex-col gap-5 md:gap-10">
                <h1 className="w-full break-words font-bold text-[38px] leading-[1] tracking-[1px]">
                  {name}
                </h1>
                {description && (
                  <MarkdownText
                    markdown={description}
                    className="text-lg leading-[1.5] tracking-[1px]"
                  />
                )}
              </div>
            </ScrollFadeWrapper>

            {showLaunchButton && (
              <div className="max-w-[250px] w-full absolute z-20 left-1/2 -translate-x-1/2 pb-10 md:pb-[80px] bottom-[200px] md:bottom-[170px]">
                {showButtonSpinner ? (
                  <div className="flex items-center justify-center h-[56px]">
                    <Spinner size="2md" variant="light" />
                  </div>
                ) : (
                  <EjcLaunchButton
                    onLaunch={onLaunch}
                    label={launchLabel}
                    disabled={launchDisabled}
                  />
                )}
              </div>
            )}
          </div>

          {/* 8thwall loading overlay — fades in once isLoading flips and assets
              are streaming in. Kept mounted so the fade animation runs both
              ways without unmount thrash. */}
          <div
            style={{
              opacity: showLoadingOverlay ? 1 : 0,
              transition: `opacity ${CONTENT_FADE_MS}ms ease-in-out ${showLoadingOverlay ? `${CONTENT_FADE_MS}ms` : "0ms"}`,
              pointerEvents: showLoadingOverlay ? "auto" : "none",
            }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center px-5"
          >
            <div className="flex w-full max-w-[420px] flex-col items-center justify-center gap-[50px] text-white translate-y-10">
              <div className="flex flex-col items-center gap-[40px]">
                <Spinner size="2md" variant="light" />
                <p className="w-full text-center text-[20px] font-medium leading-[1.25] tracking-[0.05em] text-white">
                  {loadingMessage}
                </p>
              </div>
              <div className="h-px w-[40px] bg-white/30" />
              <p className="max-w-[420px] text-center text-[20px] font-normal leading-[1.25] tracking-[0.05em] text-white">
                <span dangerouslySetInnerHTML={{ __html: loadingSafetyMessage }} />
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EjcSplash;
